import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getLabOrderRequest } from '../../../../redux/modules/laboratory/labOrder/slice';
import { sendLabReportRequest, resetSendLabReport } from '../../../../redux/modules/laboratory/labReport/slice';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem, Stack, Pagination, Checkbox } from '@mui/material';
import SearchInput from '../../../../utils/searchInput';
import { dayStartISO, dayEndISO, localTodayYMD } from '../../../../utils/dateRange';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EmailIcon from '@mui/icons-material/Email';
import PrintIcon from '@mui/icons-material/Print';
import { toast } from 'react-toastify';

const statusColors = {
    verified: 'info',
    released: 'success',
    resulted: 'warning',
};

export default function LabReports() {
    const dispatch = useDispatch();
    const { getRes: getAllRes } = useSelector(state => state.labOrder);
    const { sendReportRes, loading } = useSelector(state => state.labReport);
    const { currentBranch } = useSelector(state => state.branch);
    const { localization } = useSelector(state => state.hospitalConfig);
    const tz = localization?.timezone;
    // On-screen Ordered/Verified columns: date + 24h time in the clinic timezone
    // (e.g. GYT for Ultracare) so the time is accurate regardless of the viewer's
    // machine timezone. Only used here on the page — not on the printed report.
    const fmtDateTime = (v) => {
        if (!v) return '-';
        const d = new Date(v);
        if (isNaN(d.getTime())) return '-';
        const opts = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false };
        try { return d.toLocaleString('en-GB', tz ? { ...opts, timeZone: tz } : opts); }
        catch (e) { return d.toLocaleString('en-GB', opts); }
    };

    const [statusFilter, setStatusFilter] = useState('verified');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [emailDialog, setEmailDialog] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [email, setEmail] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [bulkLoading, setBulkLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const limit = 10;

    useEffect(() => {
        dispatch(setMainHeading("Lab Reports"));
    }, [dispatch]);

    // Server-side paginated + searchable, mirroring the Orders list (same endpoint).
    // The From/To dates filter the visible table too (not just bulk print), so the
    // list and the printed set always agree. Empty dates omit the param.
    useEffect(() => {
        dispatch(getLabOrderRequest({ page: currentPage, limit, searchQuery, status: statusFilter, dateFrom: dayStartISO(dateFrom), dateTo: dayEndISO(dateTo) }));
    }, [dispatch, currentPage, searchQuery, statusFilter, dateFrom, dateTo]);

    useEffect(() => {
        if (sendReportRes?.success) {
            toast.success(sendReportRes.message || "Report sent");
            setEmailDialog(false);
            setEmail('');
            dispatch(resetSendLabReport());
        } else if (sendReportRes && !sendReportRes.success) {
            toast.error(sendReportRes.message || "Failed to send");
            dispatch(resetSendLabReport());
        }
    }, [sendReportRes, dispatch]);

    const orders = getAllRes?.data || [];
    const pageCount = getAllRes?.dataCount > limit ? Math.ceil(getAllRes.dataCount / limit) : 0;

    // Checkbox selection for bulk print. Selection is by order id and persists
    // across pages/filters; the header checkbox toggles only the current page.
    const pageIds = orders.map(o => o._id);
    const someOnPageSelected = pageIds.some(id => selectedIds.includes(id));
    const allOnPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));
    const toggleSelect = (id) =>
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const toggleSelectAllOnPage = () =>
        setSelectedIds(prev => allOnPageSelected
            ? prev.filter(id => !pageIds.includes(id))
            : [...new Set([...prev, ...pageIds])]);

    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        setCurrentPage(1);
    };

    const handleDownloadPdf = async (orderId, labOrderId) => {
        try {
            const token = localStorage.getItem("item");
            const branchId = currentBranch?.id || '';
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_API}api/laboratory/reports/order/${orderId}/pdf`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'X-Current-Branch': branchId,
                    },
                }
            );
            if (!response.ok) throw new Error('Failed to download report');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `LabReport_${labOrderId || orderId}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            toast.error('Failed to download report');
        }
    };

    // Fetch one combined PDF from the bulk endpoint (by date range or an explicit
    // set of checked-off order ids) and open it for printing.
    const openBulkReports = async (query) => {
        try {
            setBulkLoading(true);
            const token = localStorage.getItem("item");
            const branchId = currentBranch?.id || '';
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_API}api/laboratory/reports/bulk?${query}`,
                { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'X-Current-Branch': branchId } }
            );
            if (!response.ok) {
                let msg = 'No finalized reports found';
                try { const j = await response.json(); if (j?.message) msg = j.message; } catch (e) { /* not JSON */ }
                toast.info(msg);
                return;
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            if (!win) toast.info('Allow pop-ups for this site to view and print the reports');
            // Keep the blob URL alive long enough for the new tab to load it.
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch (error) {
            toast.error('Failed to generate reports');
        } finally {
            setBulkLoading(false);
        }
    };

    // Date-range print: the viewer's local start/end of day sent as exact instants,
    // matching the Lab Orders "Print Today's Orders" flow.
    const handleBulkPrint = (fromISO, toISO) =>
        openBulkReports(`dateFrom=${encodeURIComponent(fromISO)}&dateTo=${encodeURIComponent(toISO)}`);

    const handlePrintToday = () => {
        const today = localTodayYMD();
        handleBulkPrint(dayStartISO(today), dayEndISO(today));
    };

    const handlePrintRange = () => {
        if (!dateFrom || !dateTo) { toast.info('Pick both From and To dates'); return; }
        if (dateFrom > dateTo) { toast.info('From date must be on or before To date'); return; }
        handleBulkPrint(dayStartISO(dateFrom), dayEndISO(dateTo));
    };

    // Print only the checked-off reports (selection persists across pages).
    const handlePrintSelected = () => {
        if (selectedIds.length === 0) { toast.info('Select at least one report to print'); return; }
        openBulkReports(`orderIds=${encodeURIComponent(selectedIds.join(','))}`);
    };

    const handleSendEmail = () => {
        if (!selectedOrder || !email) return;
        dispatch(sendLabReportRequest({ orderId: selectedOrder._id, email }));
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} label="Status" onChange={e => handleStatusFilter(e.target.value)}>
                        <MenuItem value="verified">Verified (Ready)</MenuItem>
                        <MenuItem value="released">Released</MenuItem>
                        <MenuItem value="resulted">Resulted</MenuItem>
                    </Select>
                </FormControl>
                <SearchInput setSearchQuery={(v) => { setSearchQuery(v); setCurrentPage(1); }} />
            </Box>

            {/* Date range: filters the table AND drives the bulk-print buttons. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>Date range:</Typography>
                <TextField
                    label="From" type="date" size="small"
                    value={dateFrom} onChange={e => { setDateFrom(e.target.value); setCurrentPage(1); }}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label="To" type="date" size="small"
                    value={dateTo} onChange={e => { setDateTo(e.target.value); setCurrentPage(1); }}
                    InputLabelProps={{ shrink: true }}
                />
                <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrintRange} disabled={bulkLoading}>
                    Print Range
                </Button>
                <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrintToday} disabled={bulkLoading}>
                    Print Today's Reports
                </Button>
                <Button variant="contained" color="success" startIcon={<PrintIcon />} onClick={handlePrintSelected} disabled={bulkLoading || selectedIds.length === 0}>
                    Print Selected{selectedIds.length ? ` (${selectedIds.length})` : ''}
                </Button>
            </Box>

            {/* Persistent selection summary — makes it clear the checked reports
                carry across pages/filters, not just the page you're looking at. */}
            {selectedIds.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={600}>
                        {selectedIds.length} report{selectedIds.length > 1 ? 's' : ''} selected (across all pages)
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => setSelectedIds([])}>Clear</Button>
                </Box>
            )}

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    size="small"
                                    checked={allOnPageSelected}
                                    indeterminate={someOnPageSelected && !allOnPageSelected}
                                    onChange={toggleSelectAllOnPage}
                                    disabled={pageIds.length === 0}
                                    inputProps={{ 'aria-label': 'select all reports on this page' }}
                                />
                            </TableCell>
                            <TableCell>Order ID</TableCell>
                            <TableCell>Patient</TableCell>
                            <TableCell>Tests</TableCell>
                            <TableCell>Priority</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Ordered</TableCell>
                            <TableCell>Verified</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orders.map(order => (
                            <TableRow key={order._id} hover selected={selectedIds.includes(order._id)}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        size="small"
                                        checked={selectedIds.includes(order._id)}
                                        onChange={() => toggleSelect(order._id)}
                                        inputProps={{ 'aria-label': `select report ${order.labOrderId}` }}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{order.labOrderId}</TableCell>
                                <TableCell>
                                    {order.patientId?.fName} {order.patientId?.lName}
                                </TableCell>
                                <TableCell>{order.tests?.length || 0} tests</TableCell>
                                <TableCell>
                                    <Chip
                                        label={order.priority}
                                        size="small"
                                        color={order.priority === 'stat' ? 'error' : order.priority === 'urgent' ? 'warning' : 'default'}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip label={order.status} color={statusColors[order.status] || 'default'} size="small" />
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(order.orderedAt)}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(order.verifiedAt)}</TableCell>
                                <TableCell align="center">
                                    <Tooltip title="Download PDF">
                                        <IconButton size="small" color="primary" onClick={() => handleDownloadPdf(order._id, order.labOrderId)}>
                                            <PictureAsPdfIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Send via Email">
                                        <IconButton size="small" color="primary" onClick={() => { setSelectedOrder(order); setEmail(order.patientId?.email || ''); setEmailDialog(true); }}>
                                            <EmailIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {orders.length === 0 && (
                            <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No orders found with status "{statusFilter}"</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {pageCount > 0 && (
                <Stack direction="row" justifyContent="center" mt={2}>
                    <Pagination count={pageCount} page={currentPage} onChange={(e, v) => setCurrentPage(v)} color="primary" />
                </Stack>
            )}

            {/* Email Dialog */}
            <Dialog open={emailDialog} onClose={() => setEmailDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Send Report - {selectedOrder?.labOrderId}</DialogTitle>
                <DialogContent sx={{ pt: '16px !important' }}>
                    <TextField
                        label="Email Address"
                        type="email"
                        fullWidth
                        required
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        size="small"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSendEmail} disabled={!email || loading}>Send</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
